import { useEffect, useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, CheckCircle2 } from 'lucide-react'

export function ServerStatusBanner() {
  const [isUpdating, setIsUpdating] = useState(false)
  const [isRestored, setIsRestored] = useState(false)
  const queryClient = useQueryClient()
  const pollIntervalRef = useRef<number | null>(null)

  useEffect(() => {
    const handleServiceUpdating = () => {
      if (isUpdating) return
      setIsUpdating(true)
      setIsRestored(false)

      // Start active healthcheck polling
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = window.setInterval(async () => {
          try {
            const res = await fetch('/health', { cache: 'no-store' })
            if (res.ok) {
              // Service is back online!
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current)
                pollIntervalRef.current = null
              }
              window.dispatchEvent(new CustomEvent('service:online'))
            }
          } catch {
            // Still offline/restarting
          }
        }, 2000)
      }
    }

    const handleServiceOnline = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      setIsUpdating(false)
      setIsRestored(true)
      void queryClient.invalidateQueries()

      // Hide the restored message after 3 seconds
      window.setTimeout(() => {
        setIsRestored(false)
      }, 3000)
    }

    window.addEventListener('service:updating', handleServiceUpdating)
    window.addEventListener('service:online', handleServiceOnline)

    return () => {
      window.removeEventListener('service:updating', handleServiceUpdating)
      window.removeEventListener('service:online', handleServiceOnline)
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [isUpdating, queryClient])

  if (!isUpdating && !isRestored) {
    return null
  }

  if (isRestored) {
    return (
      <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 bg-emerald-950/90 border border-emerald-500/40 text-emerald-100 text-xs font-medium rounded-full shadow-xl backdrop-blur-md animate-in fade-in slide-in-from-top-4 duration-300">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span>Зв'язок відновлено — дані оновлено</span>
      </div>
    )
  }

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-4 py-2 bg-[#1a1f2c]/95 border border-[#bc9050]/50 text-[#f8f3ea] text-xs font-medium rounded-full shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-top-4 duration-300">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
      </span>
      <span>Оновлення вітрини... Зв'язок відновиться автоматично</span>
      <RefreshCw className="w-3 h-3 text-[#d7b171] animate-spin shrink-0 opacity-80" />
    </div>
  )
}
